import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useTheme } from "@mui/material/styles";
import { playerSurfaceColors } from "../theme";
import { applyReceiverPlaybackBuffer } from "../utils/receiverPlaybackBuffer";

const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS = 30_000;
const WHEP_DELETE_TIMEOUT_MS = 2500;
const VIDEO_START_TIMEOUT_MS = 10_000;

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";

export type PlayerWhepProps = {
  serverUrl: string;
  platform: string;
  sensor: string;
  iceServers?: string | RTCIceServer[];
  useStandardControls?: boolean;
  controls?: boolean;
  showErrors?: boolean;
  lowLatencyHints?: boolean;
  receiverBufferMs?: number;
  fastDrainOnStart?: boolean;
  preferLowLatencyCodecs?: boolean;
  layer?: string;
  simulcastEnabled?: boolean;
  onDataReceive?: (time: number, data: unknown) => void;
  onSizeChange?: (size: { width: number; height: number }) => void;
  onStreamInfo?: (info: { videoWidth: number; videoHeight: number }) => void;
  onStateChange?: (state: string) => void;
  onSensorMessage?: (message: unknown) => void;
  onSensorError?: (error: unknown) => void;
  onError?: (error: unknown) => void;
};

export type PlayerWhepHandle = {
  reconnect: () => Promise<void>;
  disconnect: () => Promise<void>;
  getSnapshot: () => HTMLCanvasElement | null;
};

const parseIceServers = async (
  iceServers?: string | RTCIceServer[],
): Promise<RTCIceServer[] | undefined> => {
  if (!iceServers) {
    return undefined;
  }

  if (Array.isArray(iceServers)) {
    return iceServers;
  }

  try {
    const parsed = JSON.parse(iceServers);
    if (Array.isArray(parsed)) {
      return parsed as RTCIceServer[];
    }
  } catch (err) {
    // If JSON parsing fails, fall back to treating the string as a plain STUN/TURN URL
    if (iceServers.trim().length > 0) {
      return [{ urls: iceServers }];
    }
  }

  return undefined;
};

const parseWhepFragToIceInit = (frag: string): RTCIceCandidateInit | null => {
  let mid: string | null = null;
  let cand: string | null = null;
  const lines = frag.split(/\r?\n/);
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) continue;
    if (l.startsWith("a=mid:")) mid = l.slice(6).trim();
    else if (l.startsWith("a=candidate:")) cand = l.slice(2);
    else if (l.startsWith("candidate:")) cand = l;
  }
  if (!cand) return null;
  const init: RTCIceCandidateInit = { candidate: cand };
  if (mid !== null) init.sdpMid = mid;
  return init;
};

const findDataMidFromSDP = (sdp: string): string => {
  const sections = sdp.split(/^m=/m);
  for (const sec of sections) {
    if (sec.startsWith("application")) {
      const m = sec.match(/^a=mid:(.+)$/m);
      return m ? m[1].trim() : "0";
    }
  }
  return "0";
};

const describeData = (data: unknown): unknown => {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return data;
  if (data instanceof Blob) return data;
  return data;
};

const parseKlvData = (data: unknown): unknown => {
  if (typeof data !== "string") {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch (err) {
    console.debug("parseKlvData JSON parse failed", err);
    return data;
  }
};

const applyReceiverLowLatencyHints = (
  pc: RTCPeerConnection,
  track: MediaStreamTrack,
  enabled: boolean,
  receiverBufferMs: number,
) => {
  if (!enabled) return;
  try {
    const receiver = pc.getReceivers().find((r) => r.track === track);
    if (!receiver) return;
    applyReceiverPlaybackBuffer(receiver, receiverBufferMs);
    const params = receiver.getParameters?.();
    if (params && "degradationPreference" in params) {
      params.degradationPreference = "maintain-framerate";
      (receiver as any).setParameters?.(params).catch(() => undefined);
    }
  } catch (err) {
    console.debug("applyReceiverLowLatencyHints skipped", err);
  }
};

const maybeSetCodecPrefs = (
  transceiver: RTCRtpTransceiver,
  preferLowLatencyCodecs?: boolean,
) => {
  if (!preferLowLatencyCodecs) return;
  try {
    const caps = RTCRtpReceiver.getCapabilities("video");
    if (!caps || !caps.codecs) return;
    const list = caps.codecs.slice();
    const rank = (codec: any) => {
      const mime = (codec.mimeType || "").toLowerCase();
      const fmtp = (codec.sdpFmtpLine || "").toLowerCase();
      if (mime === "video/vp8") return 0;
      if (mime === "video/h264" && /profile-level-id=42/.test(fmtp)) return 1;
      if (mime === "video/h264") return 2;
      return 3;
    };
    list.sort((a, b) => rank(a) - rank(b));
    transceiver.setCodecPreferences(list);
  } catch (err) {
    console.debug("maybeSetCodecPrefs skipped", err);
  }
};

const PlayerWhep = React.forwardRef(function PlayerWhep(
  props: PlayerWhepProps,
  ref: ForwardedRef<PlayerWhepHandle | null>,
) {
  const {
    serverUrl,
    platform,
    sensor,
    iceServers,
    controls,
    useStandardControls,
    lowLatencyHints = true,
    receiverBufferMs = 0,
    fastDrainOnStart = true,
    preferLowLatencyCodecs = true,
    layer,
    onDataReceive,
    onSizeChange,
    onStreamInfo,
    onStateChange,
    onSensorMessage,
    onSensorError,
    onError,
  } = props;

  const theme = useTheme();
  const backgroundColor = playerSurfaceColors[theme.palette.mode];
  // A poster also replaces the browser's gray native loading surface.
  const poster = `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="9"><rect width="100%" height="100%" fill="${backgroundColor}"/></svg>`,
  )}`;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const resourceUrlRef = useRef<string | null>(null);
  const connectionIdentityRef = useRef<string | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const videoStartTimerRef = useRef<number | null>(null);
  const reconnectFailuresRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);
  const connectPromiseRef = useRef<Promise<void> | null>(null);
  const connectRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const videoListenersRef = useRef<{
    metadata: EventListener | null;
    resize: EventListener | null;
  }>({ metadata: null, resize: null });
  const connectionIdentity = `${serverUrl}\u0000${platform}\u0000${sensor}\u0000${layer ?? ""}`;
  const createSnapshot = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return null;
    }
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }
    ctx.drawImage(video, 0, 0, width, height);
    return canvas;
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearVideoStartTimer = useCallback(() => {
    if (videoStartTimerRef.current !== null) {
      window.clearTimeout(videoStartTimerRef.current);
      videoStartTimerRef.current = null;
    }
  }, []);

  const cleanupMedia = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (videoListenersRef.current.metadata) {
        video.removeEventListener("loadedmetadata", videoListenersRef.current.metadata);
      }
      if (videoListenersRef.current.resize) {
        video.removeEventListener("resize", videoListenersRef.current.resize);
      }
      videoListenersRef.current = { metadata: null, resize: null };
      video.srcObject = null;
      video.playbackRate = 1.0;
    }
  }, []);

  const closeSse = useCallback(() => {
    if (sseRef.current) {
      try {
        sseRef.current.close();
      } catch (err) {
        console.debug("SSE close error", err);
      }
      sseRef.current = null;
    }
  }, []);

  const disconnect = useCallback(async () => {
    clearReconnectTimer();
    clearVideoStartTimer();
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    closeSse();
    const resourceUrl = resourceUrlRef.current;
    resourceUrlRef.current = null;
    connectionIdentityRef.current = null;
    if (resourceUrl) {
      const deleteController = new AbortController();
      const deleteTimeout = window.setTimeout(
        () => deleteController.abort(),
        WHEP_DELETE_TIMEOUT_MS,
      );
      try {
        await fetch(resourceUrl, {
          method: "DELETE",
          signal: deleteController.signal,
        });
      } catch (err) {
        console.debug("WHEP DELETE error", err);
      } finally {
        window.clearTimeout(deleteTimeout);
      }
    }
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (err) {
        console.debug("RTCPeerConnection close error", err);
      }
      pcRef.current = null;
    }
    cleanupMedia();
  }, [cleanupMedia, clearReconnectTimer, clearVideoStartTimer]);

  const scheduleReconnect = useCallback((reason: string) => {
    if (!mountedRef.current) return;
    if (reconnectTimerRef.current !== null) return;

    const failure = reconnectFailuresRef.current;
    reconnectFailuresRef.current = Math.min(failure + 1, 10);
    const delay = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * 2 ** Math.min(failure, 5),
    );
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      void connectRef.current();
    }, delay);
    console.debug("PlayerWhep scheduled reconnect:", reason, delay);
  }, []);

  const handleError = useCallback(
    (err: unknown) => {
      if (onError) {
        onError(err);
      }
      scheduleReconnect("error");
    },
    [onError, scheduleReconnect],
  );

  const connect = useCallback(async () => {
    if (!serverUrl || !platform || !sensor) {
      return;
    }

    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    const promise = (async () => {
      await disconnect();
      connectionIdentityRef.current = connectionIdentity;
      abortControllerRef.current = new AbortController();

      try {
        const video = videoRef.current;
        if (!video) {
          throw new Error("Video element not ready");
        }

        const parsedIce = await parseIceServers(iceServers);
        const pc = new RTCPeerConnection({
          iceServers: parsedIce,
          iceCandidatePoolSize: 1,
        });
        pcRef.current = pc;

        const transceiver = pc.addTransceiver("video", { direction: "recvonly" });
        maybeSetCodecPrefs(transceiver, preferLowLatencyCodecs);

        pc.addEventListener("connectionstatechange", () => {
          if (pcRef.current !== pc) return;
          const state = pc.connectionState;
          if (state) {
            onStateChange?.(state);
          }
          if (state === "connected") {
            reconnectFailuresRef.current = 0;
            closeSse();
          }
          if (["failed", "disconnected", "closed"].includes(state)) {
            scheduleReconnect(`connectionstate ${state}`);
          }
        });

        pc.addEventListener("iceconnectionstatechange", () => {
          if (pcRef.current !== pc) return;
          const state = pc.iceConnectionState;
          if (state === "connected" || state === "completed") {
            closeSse();
          }
          if (["failed", "disconnected", "closed"].includes(state)) {
            scheduleReconnect(`icestate ${state}`);
          }
        });

        pc.addEventListener("track", (event) => {
          if (event.track.kind !== "video") return;
          clearVideoStartTimer();
          const [stream] = event.streams;
          applyReceiverLowLatencyHints(pc, event.track, lowLatencyHints, receiverBufferMs);
          video.srcObject = stream ?? new MediaStream([event.track]);
          void video.play().catch(() => undefined);
          if (fastDrainOnStart) {
            const prev = video.playbackRate;
            video.playbackRate = 1.2;
            window.setTimeout(() => {
              if (video.playbackRate === 1.2) {
                video.playbackRate = prev;
              }
            }, 1500);
          }
        });

        const notifyVideoDimensions = () => {
          const v = videoRef.current;
          if (!v) return;
          const width = v.videoWidth;
          const height = v.videoHeight;
          if (width && height) {
            onSizeChange?.({ width, height });
            onStreamInfo?.({ videoWidth: width, videoHeight: height });
          }
        };

        if (videoListenersRef.current.metadata) {
          video.removeEventListener("loadedmetadata", videoListenersRef.current.metadata);
        }
        if (videoListenersRef.current.resize) {
          video.removeEventListener("resize", videoListenersRef.current.resize);
        }

        const metadataListener: EventListener = () => notifyVideoDimensions();
        const resizeListener: EventListener = () => notifyVideoDimensions();
        video.addEventListener("loadedmetadata", metadataListener);
        video.addEventListener("resize", resizeListener);
        videoListenersRef.current = {
          metadata: metadataListener,
          resize: resizeListener,
        };

        const registerDataChannel = (
          label: string,
          onMessage: (value: unknown) => void,
          options?: RTCDataChannelInit,
        ) => {
          const dc = pc.createDataChannel(label, options);
          dc.onmessage = (ev) => {
            if (pcRef.current !== pc) return; // Ignore queued data from a retired playback connection.
            try {
              onMessage(describeData(ev.data));
            } catch (err) {
              console.debug(`DataChannel ${label} handler error`, err);
            }
          };
          return dc;
        };

        registerDataChannel(
          "klv-data",
          (data) => {
            const time = Date.now();
            onDataReceive?.(time, parseKlvData(data));
          },
          { ordered: false, maxRetransmits: 0 },
        );

        registerDataChannel(
          "sensor-state",
          (data) => {
            if (typeof data === "string") {
              onStateChange?.(data);
            }
          },
          { ordered: true },
        );

        registerDataChannel(
          "sensor-error",
          (data) => {
            onSensorError?.(data);
          },
          { ordered: true },
        );

        registerDataChannel(
          "sensor-message",
          (data) => {
            onSensorMessage?.(data);
          },
          { ordered: true },
        );

        let offer = await pc.createOffer();
        if (props.simulcastEnabled && props.layer) {
          // If simulcast is requested with a specific layer, prioritize it.
          offer.sdp = offer.sdp?.replace(
            /a=simulcast: recv (.*)/,
            `a=simulcast: recv rid=${props.layer}`,
          ) ?? offer.sdp;
        }

        await pc.setLocalDescription(offer);
        const localDescription = pc.localDescription;
        if (!localDescription?.sdp) {
          throw new Error("Missing local description");
        }

        const localMid = findDataMidFromSDP(localDescription.sdp);

        const headers: Record<string, string> = {
          "Content-Type": "application/sdp",
          Platform: platform,
          Sensor: sensor,
        };
        if (layer) {
          headers["Layer"] = layer;
        }

        const baseUrl = new URL(serverUrl, window.location.href).toString();
        const response = await fetch(baseUrl, {
          method: "POST",
          headers,
          body: localDescription.sdp,
          signal: abortControllerRef.current?.signal,
        });

        if (!(response.status === 200 || response.status === 201)) {
          const text = await response.text();
          throw new Error(`WHEP POST failed: ${response.status} ${text}`);
        }

        const location = response.headers.get("Location");
        if (!location) {
          throw new Error("WHEP response missing Location header");
        }
        resourceUrlRef.current = new URL(location, baseUrl).toString();

        const answerSdp = await response.text();
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
        clearVideoStartTimer();
        videoStartTimerRef.current = window.setTimeout(() => {
          videoStartTimerRef.current = null;
          const videoHasFrames =
            video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
            video.videoWidth > 0 &&
            video.videoHeight > 0;
          if (mountedRef.current && !videoHasFrames) {
            scheduleReconnect("video track was not ready after WHEP startup");
          }
        }, VIDEO_START_TIMEOUT_MS);

        const sseBase = resourceUrlRef.current;
        const sseUrl = sseBase.endsWith("/") ? `${sseBase}events` : `${sseBase}/events`;
        const sse = new EventSource(sseUrl);
        sseRef.current = sse;

        sse.addEventListener("candidate", (event) => {
          const init = parseWhepFragToIceInit((event as MessageEvent).data);
          if (init) {
            if (!init.sdpMid) init.sdpMid = localMid;
            pc.addIceCandidate(init).catch((err) => handleError(err));
          }
        });
        sse.addEventListener("end", () => {
          // Server indicates end of candidates. Nothing to do.
          closeSse();
        });
        sse.addEventListener("error", (event) => {
          console.debug("SSE error", event);
          scheduleReconnect("sse error");
        });

        pc.addEventListener("icecandidate", async (ev) => {
          if (!resourceUrlRef.current) return;
          try {
            if (ev.candidate) {
              const mid = ev.candidate.sdpMid ?? localMid;
              const frag = `a=mid:${mid}\r\na=${ev.candidate.candidate}\r\n`;
              await fetch(resourceUrlRef.current, {
                method: "PATCH",
                headers: { "Content-Type": "application/trickle-ice-sdpfrag" },
                body: frag,
              });
            } else {
              const mid = localMid;
              const frag = `a=mid:${mid}\r\na=end-of-candidates\r\n`;
              await fetch(resourceUrlRef.current, {
                method: "PATCH",
                headers: { "Content-Type": "application/trickle-ice-sdpfrag" },
                body: frag,
              });
            }
          } catch (err) {
            handleError(err);
          }
        });
      } catch (err) {
        const aborted = isAbortError(err);
        await disconnect();
        if (!aborted) {
          handleError(err);
        }
      } finally {
        connectPromiseRef.current = null;
      }
    })();

    connectPromiseRef.current = promise;
    return promise;
  }, [
    disconnect,
    connectionIdentity,
    serverUrl,
    platform,
    sensor,
    iceServers,
    preferLowLatencyCodecs,
    lowLatencyHints,
    receiverBufferMs,
    fastDrainOnStart,
    layer,
    props.simulcastEnabled,
    onDataReceive,
    onSizeChange,
    onStreamInfo,
    onSensorMessage,
    onSensorError,
    onStateChange,
    handleError,
    scheduleReconnect,
    clearVideoStartTimer,
  ]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearReconnectTimer();
      clearVideoStartTimer();
      void disconnect();
    };
  }, [clearReconnectTimer, clearVideoStartTimer, disconnect]);

  useEffect(() => {
    let cancelled = false;
    void connect().then(() => {
      if (
        !cancelled &&
        mountedRef.current &&
        connectionIdentityRef.current !== connectionIdentity
      ) {
        scheduleReconnect("stream configuration changed during connection startup");
      }
    });
    return () => {
      cancelled = true;
      abortControllerRef.current?.abort();
    };
  }, [connect, connectionIdentity, scheduleReconnect]);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        const pc = pcRef.current;
        const bad =
          !pc ||
          ["failed", "disconnected", "closed"].includes(pc.iceConnectionState) ||
          ["failed", "disconnected", "closed"].includes(pc.connectionState);
        if (bad) {
          scheduleReconnect("visibilitychange");
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [scheduleReconnect]);

  useImperativeHandle(
    ref,
    () => ({
      reconnect: async () => {
        await connect();
      },
      disconnect: async () => {
        await disconnect();
      },
      getSnapshot: () => createSnapshot(),
    }),
    [connect, disconnect, createSnapshot],
  );

  const videoControls = useMemo(() => {
    if (typeof controls === "boolean") {
      return controls;
    }
    if (typeof useStandardControls === "boolean") {
      return useStandardControls;
    }
    return true;
  }, [controls, useStandardControls]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      controls={videoControls}
      poster={poster}
      style={{ width: "100%", height: "100%", backgroundColor }}
    />
  );
});

export default PlayerWhep;
